// Is the TikTok story studio really there? (Owner, 2026-10-03: the live site
// offered "Make a story video" and every click ended in "Local story service
// is not reachable", because the story pipeline only ever ran on the desktop
// machine and nothing on Render answers STORY_SERVICE_URL.)
//
// GET /api/web/story/config (routes/story.js) says whether the server can
// reach the story service right now. Until it says yes, every way into the
// tool (the landing card, the compass stop, the pitch button, the Director
// console's workflow, the /tiktok page) shows "story videos are coming soon"
// in plain words and offers no button that can only fail. The answer is
// asked for once per page load and shared by every surface.

import { useEffect, useState } from "react";
import { onToolAvailabilityChange } from "@/lib/tool-availability";

export interface StoryConfig {
  available: boolean;
  credits?: number;
}

let cached: boolean | null = null;
let inflight: Promise<boolean> | null = null;
const listeners = new Set<(v: boolean) => void>();

/** The server's answer, fetched once (or again with fresh); false on any failure (a tool that cannot run is never offered). */
export function fetchStoryAvailability(fresh = false): Promise<boolean> {
  if (cached !== null && !fresh) return Promise.resolve(cached);
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const r = await fetch("/api/web/story/config", { headers: { Accept: "application/json" } });
      if (!r.ok) return false;
      const j = (await r.json()) as Partial<StoryConfig> | null;
      return j?.available === true;
    } catch {
      return false;
    }
  })().then((v) => {
    const changed = cached !== v;
    cached = v;
    inflight = null;
    if (changed || listeners.size) for (const fn of listeners) fn(v);
    return v;
  });
  return inflight;
}

/** The answer so far: false until the server has said yes. */
export function storyAvailableNow(): boolean {
  return cached === true;
}

/** False until the server says the story service is reachable; re-renders once it answers. */
export function useStoryAvailability(): boolean {
  const [available, setAvailable] = useState<boolean>(storyAvailableNow);
  useEffect(() => {
    // Always listen: the answer can change after this surface mounted (a late /config, a test's stub).
    listeners.add(setAvailable);
    if (cached !== null) setAvailable(cached);
    else void fetchStoryAvailability();
    // Asked again whenever the site-wide check (lib/tool-availability) changes,
    // so the tool comes back by itself (owner 2026-10-16).
    const off = onToolAvailabilityChange(() => void fetchStoryAvailability(true));
    return () => { listeners.delete(setAvailable); off(); };
  }, []);
  return available;
}

/** Tests: pretend the server answered (or forget its answer). */
export function setStoryAvailabilityForTests(value: boolean | null): void {
  cached = value;
  inflight = null;
  if (value !== null) for (const fn of listeners) fn(value);
}
